import { isMidnight } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Morpho Midnight — a fixed-rate ORDER BOOK (offers from an HTTP API, not a
 * pool). Deployed on Base and Ethereum; nothing here is chain-specific.
 *
 * The decisive fact: **being past due IS the liquidation trigger**. An
 * unrepaid loan can be liquidated after maturity regardless of health or LTV,
 * which no variable-rate intuition prepares a user for. Second: over-repay
 * REVERTS (a `uint128` underflow with no dedicated error), so the repay must
 * be sized exactly.
 */
export const midnightAdapter: TermAdapter = {
  id: 'midnight',
  matches: isMidnight,
  profileId: () => 'midnight.orderbook@v1',
  build: () => ({
    supply: {
      exit: {
        mode: 'market-sale',
        settlement: 'sync',
        // Selling units into the bid side — there may be no bid.
        priceRisk: 'market-price',
        partialAllowed: true,
      },
      counterparty: {
        kind: 'orderbook',
        solvency: 'overcollateralized',
        // Bad debt is socialised to lenders via `lossFactor`.
        socializedLoss: true,
      },
      principal: { protected: false, risks: ['bad-debt'] },
      maturity: { atMaturity: 'stops-earning' },
    },
    borrow: {
      debtShape: 'static-face',
      exit: {
        // Face value, 1:1, neither fee nor rebate — so paying early is not a
        // saving, it just gives up the time value.
        earlyRepay: 'free',
        atMaturityCost: 'face',
        lateBehaviour: 'liquidatable',
        partialAllowed: true,
        overRepayReverts: true,
      },
      maturity: { atMaturity: 'liquidatable' },
      liquidation: { trigger: 'price-and-time', seizure: 'proportional' },
      counterparty: { kind: 'orderbook', solvency: 'overcollateralized' },
    },
    constraints: { positionModel: 'account' },
  }),
}
